import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { ol, ul } from './helpers';
import { PRODUCTS_AND_SERVICES_ADVANTAGES_NAMES } from '../Sections/ProductsAndServices/ProductAndServicesAdvantages/utils/form';
import { PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES } from '../Sections/ProductsAndServices/ProductAndServicesDeliveryAndSupport/utils/form';
import {
  KEY_FEATURES_NAMES,
  PRODUCT_AND_SERVICES_ARRAY_NAMES,
  PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES,
} from '../Sections/ProductsAndServices/ProductAndServicesDescription/utils/form';
import {
  PRODUCTS_AND_SERVICES_R_AND_D_NAMES,
  TIMELINE_ROW_NAMES,
} from '../Sections/ProductsAndServices/ProductAndServicesRAndD/utils/form';

const KEY_FEATURES_INSTRUCTIONS = {
  [KEY_FEATURES_NAMES.FEATURE]: {
    name: 'Product/Service Characteristics',
    instruction:
      'Enumerate the key features of the product concisely, highlighting its main functionalities and benefits.',
    exampleOne: '',
    exampleTwo: '',
  },
  [KEY_FEATURES_NAMES.DESCRIPTION]: {
    name: 'Product/Service Characteristics',
    instruction:
      'Highlight any innovative aspects or technological advancements that set it apart in the market. Clearly articulate the functionality, specifications, and design elements that contribute to its uniqueness.',
    exampleOne: ol([
      'Powerful: Engineered to provide robust performance in demanding conditions.',
      'Continuous Track Design: A unique track system offers superior traction and flotation, especially on heavy or wet soils.',
      'Patented Rubber Coating: Minimizes soil pressure and impact, reducing crop damage and compaction.',
    ]),
    exampleTwo: ol([
      'Accuracy: translation services and AI-powered tools prioritize precision and correctness, ensuring that communication remains faithful to the original message across languages.',
      'Efficiency: Through advanced AI technology, the company optimizes the translation process, enabling faster turnaround times without sacrificing quality, thus enhancing overall productivity.',
    ]),
  },
};

const DEVELOPMENT_TIMELINE_INSTRUCTIONS = {
  [TIMELINE_ROW_NAMES.STAGE]: {
    name: 'Development Timeline',
    instruction:
      'When addressing inquiries about the project timeline, follow these steps:' +
      ol([
        'Outline key stages from concept to launch.',
        'Specify estimated durations for each stage.',
        'Indicate what stage you are currently at.',
      ]),
    exampleOne: ul([
      'Concept & Design: 6 months.',
      'Prototyping & Testing: 9 months.',
      'Manufacturing Setup: 3 months.',
      'Marketing & Launch Preparation: 2 months.',
      'Currently in the manufacturing setup stage.',
    ]),
    exampleTwo: ul([
      'Concept phase: 3 months.',
      'Design and prototyping: 6 months.',
      'Testing and iteration: 4 months.',
      'Finalization and production: 3 months.',
      'Currently in testing and iteration stage.',
    ]),
  },
};

const KEY_FEATURES_AND_BENEFITS_INSTRUCTIONS = {
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION]: KEY_FEATURES_INSTRUCTIONS,
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.FILE]: {
    name: '',
    instruction:
      'Include a high-quality photo of the product/service. Ensure the image is well-lit and professionally taken.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE]: {
    name: 'Product/Service Name',
    instruction:
      'Specify the name of the offering. Use a clear and professional title that reflects the core nature of the product or service.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const QUALITY_CERTIFICATIONS_DESCRIPTION_INSTRUCTIONS = {
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE]: {
    name: 'Quality Certifications Name',
    instruction: 'Provide a clear list of relevant certifications such as ISO 9001 for quality management. ',
    exampleOne: 'EcoGrain Certified',
    exampleTwo: 'EnviroCrop Certified',
  },

  [PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION]: {
    name: 'Quality Certifications Description',
    instruction:
      'Provide a clear list of relevant certifications such as ISO 9001 for quality management. Briefly explain the significance of each certification and offer to provide further details if needed.',
    exampleOne:
      'EcoGrain Certified EcoGrain Certified guarantees grains are produced using environmentally friendly practices, such as organic farming methods, minimal use of pesticides, and sustainable water management. This certification assures consumers that the product aligns with ecological standards, promoting biodiversity and conservation efforts.',
    exampleTwo:
      'EnviroCrop Certified EnviroCrop Certified signifies crops grown with a focus on environmental stewardship and sustainability. This certification guarantees that agricultural practices, such as soil conservation, crop rotation, and natural pest control methods, are employed to minimize ecological impact and preserve natural resources. Consumers can trust that products bearing this certification support eco-friendly farming practices and contribute to environmental conservation efforts.',
  },
};

const PARENTS_OR_TRADEMARKS_DESCRIPTION_INSTRUCTIONS = {
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE]: {
    name: 'Intellectual Property Name',
    instruction:
      'Provide a succinct list of relevant patents and trademarks. Include their names, registration numbers, and brief descriptions of their significance.',
    exampleOne: 'InnoGrainTM Technology',
    exampleTwo: 'SmartSeed Technology',
  },
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION]: {
    name: 'Intellectual Property Description',
    instruction:
      'Provide a succinct list of relevant patents and trademarks. Include their names, registration numbers, and brief descriptions of their significance.',
    exampleOne:
      'InnoGrainTM Technology encompasses patented processes and innovations in grain breeding, cultivation, and preservation. It ensures superior grain varieties with enhanced traits, such as higher yields, disease resistance, and nutritional value. This proprietary technology secures the company`s competitive edge in the market and safeguards its unique advancements in agricultural science.',
    exampleTwo:
      'SmartSeed Technology incorporates advanced techniques and genetic innovations to develop high-performing seed varieties. These seeds are engineered for resilience against pests and diseases, increased yield potential, and optimized nutrient uptake. By utilizing SmartSeed Technology, farmers can cultivate crops more efficiently, reducing the need for chemical inputs and promoting sustainable agriculture practices while ensuring consistent crop quality and productivity.',
  },
};

const DESCRIPTION_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PRODUCTS_OR_SERVICES_AND_THEIR_PURPOSE]: {
    name: 'Product/Service Overview',
    instruction:
      'Provide a structured list of your products or services. Group them into categories or highlight any flagship offerings that define your business. Highlight the key characteristics or qualities that differentiate your products or services from your competitors, such as innovation, quality, or unique features.',
    exampleOne:
      'The company specializes in the production of powerful tracked tractors designed for heavy or wet soils. Products include:' + 
      ol([
        'Crawler tractors',
        'Agricultural machinery',
        'Environmental solutions'
      ]),

    exampleTwo:
      'The company offers a range of products and services designed to optimize global communication and engagement processes. Offerings include:' + 
      ol([
        'Translation services',
        'Artificial intelligence-based translation tools',
        'Solutions for optimizing communication',
        'Innovative technological solutions'
      ]),
  },
  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.KEY_FEATURES_AND_BENEFITS]: KEY_FEATURES_AND_BENEFITS_INSTRUCTIONS,
  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_STANDARDS]: {
    name: 'Quality Standards',
    instruction:
      'State that your products/services adhere to globally recognized quality standards, like ISO 9001:2015, emphasizing consistency and excellence. Briefly outline any additional specific standards relevant to your industry.',
    exampleOne:
      'The company`s products adhere to globally recognized quality standards, including ISO 9001:2015, ensuring consistency and excellence in manufacturing processes and product quality. Additionally, the company complies with industry-specific standards such as ASTM International for materials and performance testing in the agricultural machinery sector.',
    exampleTwo:
      'The company`s products and services adhere to globally recognized quality standards such as ISO 9001:2015, ensuring consistency and excellence in operations. Additionally, the company follows industry-specific standards like ASTM F2575 for translation quality assurance, maintaining high-quality standards in all our deliverables.',
  },
  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_CERTIFICATIONS]: QUALITY_CERTIFICATIONS_DESCRIPTION_INSTRUCTIONS,
  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PATENTS_OR_TRADEMARKS]: PARENTS_OR_TRADEMARKS_DESCRIPTION_INSTRUCTIONS,
};

const ADVANTAGES_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_ADVANTAGES_NAMES.PRODUCT_VALUE]: {
    name: 'Customer Value',
    instruction:
      'Clearly state the primary value your product offers and the problem it solves. Elaborate on the benefits customers will derive from using your product. Highlight how your product makes their lives easier, saves time or money, or improves their overall experience.',
    exampleOne:
      'By minimizing soil compaction and maximizing traction, the tracked tractors enable farmers to work more effectively, completing tasks faster and more precisely.',
    exampleTwo:
      'The language services platform facilitates seamless communication across linguistic barriers, enabling clients to engage with global audiences and expand their reach effectively.',
  },
};

const R_AND_D_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.READINESS_LEVEL]: {
    name: 'Production and Implementation Readiness',
    instruction:
      'Convey the progress succinctly: "Fully launched" indicates the product/service is available and operational;  "Final testing phase" implies completion is imminent; "Under development" suggests ongoing work before market readiness; "Concept phase" conveys initial stages of development.',
    exampleOne:
      'The tracked tractors are fully launched, available for purchase and operational, with extensive field testing validating performance and reliability.',
    exampleTwo: 'The translation app is currently under development, with expected launch in six months.',
  },
  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.CONDUCTED_ACTIVITIES]: {
    name: 'R&D Progress',
    instruction:
      'Provide a concise summary:' +
      ol([
        'Highlight key research areas and objectives.',
        'Mention results any breakthroughs, innovations, or discoveries.',
        'Offer to share detailed reports or findings upon request.',
      ]),
    exampleOne:
      'R&D focused on enhancing tractor efficiency, reducing environmental impact, and improving soil health. Breakthroughs include patented continuous track design. Detailed reports available upon request.',
    exampleTwo:
      'R&D focuses on enhancing translation accuracy and speed using AI algorithms. Breakthroughs include a 20% increase in translation efficiency. Detailed reports available upon request.',
  },
  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.TIMELINES]: DEVELOPMENT_TIMELINE_INSTRUCTIONS,
  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.RESOURCES]: {
    name: 'Required Resources',
    instruction:
      'Provide a comprehensive overview of the critical resources required to successfully develop your product or service. Categorize these resources into technical, financial, human, and other pertinent categories.',
    exampleOne:
      'Critical resources include:' +
      ol([
        'Technical: Advanced manufacturing equipment, software for design and simulation.',
        'Financial: Funding for R&D, manufacturing setup, and marketing.',
        'Human: Engineers, technicians, marketing specialists.',
        'Other: Regulatory approvals, supply chain partnerships.',
      ]),
    exampleTwo: ol([
      'Technical: Software development tools, AI algorithms',
      'Financial: Funding for research, equipment procurement',
      'Human: Software engineers, AI specialists',
      'Other: Market research data, legal support',
    ]),
  },
  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.WEAKNESSES]: {
    name: 'Product Deficiencies and Solutions',
    instruction:
      'Acknowledge any deficiencies or weaknesses in the current iteration of your product. Clearly articulate your awareness of customer feedback or industry standards highlighting these deficiencies. Delve into the specific strategies and timelines for addressing the identified deficiencies.',
    exampleOne:
      'The company is addressing concerns regarding tractor`s fuel efficiency by optimizing engine performance and aerodynamics. Targeting implementation within the next year to meet industry standards and improve operational cost-effectiveness.',
    exampleTwo:
      "The company acknowledges user feedback regarding occasional translation inaccuracies. The company's team is enhancing AI algorithms to improve accuracy by 15% within the next three months.",
  },
};

const DELIVERY_AND_SUPPORT_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES.LOGISTICS]: {
    name: 'Logistics and Delivery',
    instruction:
      'Describe your shipping process, highlighting any partnerships with carriers or fulfillment centers. Mentione any strategies for addressing shipping issues promptly.',
    exampleOne:
      'The company partners with reputable carriers for global shipping. Orders are processed swiftly, with real-time tracking available. Returns are managed through a streamlined process, ensuring prompt resolution and customer satisfaction.',
    exampleTwo:
      'The company partners with reliable carriers for worldwide shipping. Orders are processed within 24 hours, with tracking available. The company offers hassle-free returns and promptly address shipping issues through dedicated customer support.',
  },
  [PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES.POST_PURCHASE]: {
    name: 'Support and Maintenance Services',
    instruction:
      'Provide a concise overview of your comprehensive customer care initiatives. Start by highlighting the range of assistance available, such as technical support, troubleshooting guides, and repair services. Clearly outline the channels through which customers can access support, including online resources, dedicated helplines, and service centers.',
    exampleOne:
      'The company provides comprehensive post-purchase support including technical assistance, troubleshooting guides, and repair services. Customers access support through online resources, dedicated helplines, or visiting our service centers globally.',
    exampleTwo:
      'The company offers 24/7 technical support, troubleshooting guides, and repair services. Customers can access support via online resources, dedicated helplines, or visit our service centers worldwide.',
  },
  [PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES.ANY_WARRANTY]: {
    name: 'Warranty and Return Programs',
    instruction:
      'Clearly detail any warranties provided with your products, including coverage duration and conditions. Describe your return process, highlighting any eligibility criteria, return window, and procedures for initiating returns.',
    exampleOne:
      'The products come with a 1-year warranty covering manufacturing defects. Returns are accepted within 30 days of purchase for eligible items in original condition. Customers initiate returns by contacting our customer support team for a return authorization and shipping instructions.',
    exampleTwo:
      'The language platform offers a satisfaction guarantee. Users can request a full refund within 30 days of purchase if they are not completely satisfied with the platform`s features and effectiveness. Additionally, we provide a 12-month warranty for any technical issues or malfunctions that may arise. If users encounter any problems with the platform during this period, they can contact our customer support team for assistance and potential replacement or repair options.',
  },
};

export const PRODUCTS_AND_SERVICES_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DESCRIPTION]: DESCRIPTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.ADVANTAGES]: ADVANTAGES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.R_AND_D]: R_AND_D_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DELIVERY_AND_SUPPORT]: DELIVERY_AND_SUPPORT_INSTRUCTIONS,
};
